import { cn } from "@/lib/utils"

import type { ReportItem } from "../../config/reports-data"

/**
 * "Report in Progress" card — an "unlocks in" banner over the purchased report
 * row (cover, title, loader), 1:1 with Figma "Additional_Report_Preview". The
 * banner comes in two sizes:
 *  - success sheet (Reports_module_starter_popover): 6px padding around a 20px pill;
 *  - reports list row (Reports_module_Processing), `compact`: tighter — 4px
 *    padding around a 14px pill.
 * The 54px row underneath is the same in both.
 *
 * `dimmed` renders the locked/processing look from Reports_module_Processing —
 * cover at 60% opacity and a purple-light/60 title — used in the reports list.
 * The success sheet keeps the cover and title at full strength.
 */
export function ReportUnlockingCard({
  report,
  remainingLabel,
  dimmed = false,
  compact = false,
}: {
  report: ReportItem
  remainingLabel: string
  dimmed?: boolean
  compact?: boolean
}) {
  return (
    // Back layer: the purple block (same radius). The "unlocks in" banner sits on
    // it up top and it shows through at the grey row's rounded top corners.
    <div className="w-full overflow-hidden rounded-xl bg-main-purple">
      <div className={cn("flex items-center justify-center gap-1", compact ? "py-1" : "py-1.5")}>
        <span className="font-body text-sm font-semibold text-white">Your reading unlocks in</span>
        <span
          className={cn(
            "flex items-center justify-center rounded-[10px] bg-white px-2 font-body text-xs font-bold tabular-nums text-dark-base",
            compact ? "h-[14px] leading-none" : "h-5",
          )}
        >
          {remainingLabel}
        </span>
      </div>

      {/* Front layer: the report row. Solid dark-base + a purple-light/10 wash =
          the same grey as the big card's surface (not dark-surface, which matched
          the sheet and read as black). Rounded top corners reveal the purple. */}
      <div className="relative flex h-[54px] items-center overflow-hidden rounded-t-xl bg-dark-base pr-3">
        <span className="pointer-events-none absolute inset-0 bg-purple-light/10" />
        <img
          src={report.cover}
          alt={report.title}
          className={cn("relative h-[54px] w-20 shrink-0 object-cover", dimmed && "opacity-60")}
        />
        <span
          className={cn(
            "relative min-w-0 flex-1 px-3 font-display text-base font-medium",
            dimmed ? "text-purple-light/60" : "text-white",
          )}
        >
          {report.title}
        </span>
        <span className="relative size-6 shrink-0 animate-spin rounded-full border-2 border-purple-light/20 border-t-purple-light" />
      </div>
    </div>
  )
}
